/**
 * MessageBubble — renders a single user or assistant message with parts
 */
import { useState, useRef, useEffect, useId } from 'react'
import { ChevronRight, ChevronDown, Pencil, Copy, Check, Layers, Loader2, CornerDownRight, AlertTriangle } from 'lucide-react'
import { EditMessageButtons } from './EditMessageButtons.js'
import { getPersona } from '../core/persona.js'
import { ToolCluster } from './ToolCluster.js'
import { StreamingText } from './StreamingText.js'
import { MarkdownContent } from './MarkdownContent.js'
import { MessageActionsMenu, type MessageAction } from './MessageActionsMenu.js'
import { AttachmentChip } from './AttachmentChip.js'
import { ClearedAttachmentChip } from './ClearedAttachmentChip.js'
import { SnapshotPreview } from './SnapshotPreview.js'
import type { MessagePart } from './hooks.js'

/** Format duration in human-readable form */
function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`
  const s = ms / 1000
  if (s < 60) return `${s.toFixed(1)}s`
  const m = Math.floor(s / 60)
  const rem = Math.round(s % 60)
  return `${m}m ${rem}s`
}

/** Format timestamp as relative or absolute time */
function formatTimestamp(ts: number): string {
  const diff = Date.now() - ts
  if (diff < 60_000) return 'just now'
  if (diff < 3600_000) return `${Math.floor(diff / 60_000)}m ago`
  if (diff < 86400_000) return `${Math.floor(diff / 3600_000)}h ago`
  return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

/**
 * A per-message amount, in whatever currency the caller says it is.
 *
 * The `$` used to be hardcoded here while the value came from SQLite's
 * `message.cost` — the RESALE cost in USD — and sat directly beneath a session
 * total that is billing-native. Two currencies in one column, the smaller one
 * mislabelled.
 *
 * `costCurrency` is now required alongside the amount, and the caller converts
 * before passing it. There is deliberately no default: a default is how the
 * literal came back.
 */
function formatMessageCost(cost: number, currency: string): string {
  const digits = cost < 0.01 ? 4 : cost < 1 ? 3 : 2
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(cost)
  } catch {
    // Never silently relabel as USD — that is the defect this replaced.
    return `${currency.toUpperCase()} ${cost.toFixed(digits)}`
  }
}

/** Small copy-to-clipboard button with checkmark feedback */
function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)
  const handleCopy = () => {
    navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }
  return (
    <button
      onClick={handleCopy}
      className="p-0.5 rounded themed-text-muted hover:themed-text hover:themed-bg-tertiary transition-colors"
      title="Copy message"
    >
      {copied ? <Check size={14} /> : <Copy size={14} />}
    </button>
  )
}

/** Expandable compaction block showing the AI summary of older messages.
 *
 *  The header used to be bare muted 10px text between two rules — it read as a
 *  section LABEL, not a control, so the summary underneath went unread. It is
 *  now a pill with a border, a hover fill and a rotating chevron, and the
 *  panel scrolls itself into view on open: the block usually sits well above
 *  the latest turn, so expanding it otherwise pushed content down somewhere
 *  the reader could not see. */
export function CompactionBlock({ summary, at, covered }: {
  summary?: string
  at?: number
  /** How many messages this boundary summarized — everything since the PREVIOUS
   *  boundary, which is all a pass ever covers. Shown because its absence was
   *  actively misleading: on an already-compacted session a new /compact
   *  legitimately covers only the last exchange, and with nothing on screen
   *  saying so it reads as the feature having failed. Omitted when unknown. */
  covered?: number
}) {
  const [expanded, setExpanded] = useState(false)
  const [copied, setCopied] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)
  const panelId = useId()

  useEffect(() => {
    if (!expanded) return
    const el = panelRef.current
    if (!el) return
    // `block: 'nearest'` is doing real work in both directions: it is a no-op
    // when the panel is already fully visible, and — per spec — aligns the
    // START edge when the panel is TALLER than the scrollport, so a long
    // summary is read from its beginning rather than its end.
    //
    // Not fighting MessageList's auto-scroll: that only fires on a change in
    // message COUNT, and expanding changes no message.
    const reduced = typeof window !== 'undefined'
      && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    el.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'nearest' })
  }, [expanded])

  return (
    <div className="my-3">
      <button
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
        aria-controls={panelId}
        className="w-full flex items-center gap-2 group cursor-pointer"
        data-testid="compaction-toggle"
      >
        <div className="flex-1 border-t themed-border group-hover:themed-border-secondary transition-colors" />
        <span
          className="inline-flex items-center gap-1.5 text-[10px] themed-text-muted uppercase tracking-wider
                     px-2 py-0.5 rounded-full border themed-border
                     group-hover:themed-text group-hover:themed-bg-tertiary
                     transition-colors"
        >
          <Layers size={10} className="shrink-0" />
          {/* `min-w-0` + `truncate`: the pill sits between two flex-1 rules, and
              on a narrow (mobile) transcript the label would otherwise force the
              row wider than the container. */}
          <span className="min-w-0 truncate">
            {covered && covered > 0
              // Compaction is INCREMENTAL: each pass summarizes what happened
              // since the last one, so a small number here is correct on a
              // session that has been compacted before, not a failure.
              ? `Context summary · ${covered} message${covered === 1 ? '' : 's'}`
              : 'Context summary'}
          </span>
          {!!at && (
            // Which boundary this is, when a long session has several. Time
            // only — the date is almost always today, and the pill is the one
            // place on the row with no room to spare.
            <span className="shrink-0 normal-case tracking-normal opacity-70">
              {new Date(at).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
            </span>
          )}
          <ChevronRight
            size={10}
            className={`shrink-0 transition-transform duration-200 ${expanded ? 'rotate-90' : ''}`}
          />
        </span>
        <div className="flex-1 border-t themed-border group-hover:themed-border-secondary transition-colors" />
      </button>
      {expanded && (
        <div id={panelId} ref={panelRef} className="scroll-mt-4">
          {summary ? (
            <div className="mt-2 rounded-md themed-bg-tertiary group/panel relative">
              {/* Copy: a summary is the thing you paste into a ticket or a
                  handover, and it was the one block of chat text with no way to
                  lift it out. Hover reveals it; `focus-visible` keeps it
                  reachable by keyboard, and `pointer-coarse` pins it visible on
                  touch, where there is no hover at all. */}
              <button
                onClick={() => {
                  void navigator.clipboard?.writeText(summary).then(() => {
                    setCopied(true)
                    setTimeout(() => setCopied(false), 1500)
                  })
                }}
                aria-label={copied ? 'Summary copied' : 'Copy summary'}
                className="absolute top-1.5 right-1.5 z-10 p-1 rounded cursor-pointer
                           themed-text-muted hover:themed-text hover:themed-bg-secondary
                           opacity-0 group-hover/panel:opacity-100 focus-visible:opacity-100
                           pointer-coarse:opacity-100 transition-opacity"
              >
                {copied ? <Check size={12} /> : <Copy size={12} />}
              </button>
              {/* `min-w-0` stops a long unbreakable path from widening the
                  panel past the transcript on a narrow viewport; the preset's
                  `overflow-wrap` breaks the path itself. */}
              <div className="min-w-0 px-3 py-2 pr-8 text-xs themed-text-secondary leading-relaxed overflow-hidden">
                <MarkdownContent content={summary} />
              </div>
            </div>
          ) : (
            <div className="mt-2 px-3 py-2 rounded-md themed-bg-tertiary text-xs themed-text-muted italic">
              Earlier messages were summarized to free up context space.
            </div>
          )}
        </div>
      )}
    </div>
  )
}

interface Props {
  role: string
  parts: MessagePart[]
  /** Text currently being streamed (assistant only) */
  streamingText?: string
  /** Reasoning currently being streamed */
  streamingReasoning?: string
  /** Model ID for metadata footer (assistant only) */
  modelID?: string
  /** Persona ID for metadata footer */
  personaId?: string
  /** Response duration in ms (assistant only) */
  duration?: number
  /**
   * The settled cost of this message: amount AND the currency it is in.
   *
   * ONE optional object rather than two independently-optional fields. The
   * previous shape said in prose that the currency was "required with `cost`"
   * while the types allowed either alone, and the renderer closed the gap with
   * `costCurrency ?? 'USD'` — which is exactly the silent relabelling the pair
   * exists to prevent. Absent means no badge; there is no half-present state
   * to default.
   */
  settledCost?: { amount: number; currency: string }
  /** Message creation timestamp (epoch ms) */
  timeCreated?: number
  /** User turn that was sent mid-run and steered into the running turn (#481
   *  item 4). Renders a small "steered into this turn" marker so a steer the
   *  model folded into another answer is explainable rather than looking ignored. */
  steer?: boolean
  /** E7 Codex round 8: the steer's post had no answer — shown as "not confirmed" (translated by the list) until a reload
   *  settles it. */
  steerUnconfirmed?: { label: string; hint: string }
  /** Turn actually finished (timeCompleted set by `complete` / DB load).
   *  The metadata footer gates on this — `!streamingText` alone is true
   *  mid-turn too (e.g. paused awaiting a permission), which showed the
   *  footer before the turn was done. */
  isComplete?: boolean
  /** Display option: show reasoning blocks */
  showReasoning?: boolean
  /** Display option: show tool call details (block tools render as one-liners when false) */
  showToolDetails?: boolean
  /** Display option: show model info footer */
  showModelInfo?: boolean
  /** Display option: show timestamp */
  showTimestamps?: boolean
  /** Display option: wrap code lines */
  wrapLines?: boolean
  /** Message ID for action menu */
  messageId?: string
  /** Callback for message actions (user messages only) */
  onMessageAction?: (action: MessageAction, messageId: string) => void
  /** Whether the session has file changes (for the restore-versions options) */
  hasChanges?: boolean
  /** Workspace ID for subagent message fetching */
  workspaceId?: string
  /** Session ID (#639 Task 5.2) — threaded to `AttachmentChip` so a
   *  handle-bearing file part can open the live-gated `AttachmentViewer`. */
  sessionId?: string | null
  /** Whether this message is currently in edit mode */
  isEditing?: boolean
  /** Start editing this message */
  onEditStart?: (messageId: string) => void
  /** Cancel editing */
  onEditCancel?: () => void
  /** Send the edited message */
  onEditSend?: (messageId: string, editedText: string) => void
  /** Open a file by VFS path */
  onOpenFile?: (path: string, at?: { line?: number }) => void
  /** A `file` attachment part was moved to the drive (#662) — patches chat
   *  state so the chip re-points without a reload. Threaded to `AttachmentChip`. */
  onMoved?: (partId: string, drive: { fileId: string; path: string }) => void
  /** Render controls (model selector, attachments, etc.) inside edit mode */
  renderEditControls?: (editText: string, onCancel: () => void, onSend: () => void) => React.ReactNode
}

/** Consolidated reasoning cluster — groups consecutive "thoughts" into one
 *  collapsible line (collapsed by default; shows total time + step count), and
 *  each thought inside is also collapsed by default, mirroring how a tool
 *  cluster collapses its rows. */
function ReasoningCluster({ parts }: { parts: Array<Extract<MessagePart, { type: 'reasoning' }>> }) {
  const [open, setOpen] = useState(false)
  const [openThoughts, setOpenThoughts] = useState<Set<string>>(new Set())
  const fmt = (ms: number) => ms < 1000 ? `${Math.round(ms)}ms` : `${(ms / 1000).toFixed(1)}s`
  const dur = (p: Extract<MessagePart, { type: 'reasoning' }>) =>
    p.time?.end != null ? Math.max(0, p.time.end - p.time.start) : undefined
  const totalMs = parts.reduce((s, p) => s + (dur(p) ?? 0), 0)
  const n = parts.length
  return (
    <div className="mb-1">
      <button
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-1 text-[10px] themed-text-muted hover:themed-text transition-colors"
      >
        {open ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
        <span className="italic">{totalMs > 0 ? `Thought for ${fmt(totalMs)}` : 'Thought'}</span>
        {n > 1 && <span className="opacity-60">· {n} steps</span>}
      </button>
      {open && (
        <div className="mt-0.5 pl-2 border-l-2 border-blue-500/20 space-y-0.5">
          {parts.map((p, i) => {
            const d = dur(p)
            const tOpen = openThoughts.has(p.id)
            return (
              <div key={p.id}>
                <button
                  onClick={() => setOpenThoughts(prev => {
                    const next = new Set(prev)
                    tOpen ? next.delete(p.id) : next.add(p.id)
                    return next
                  })}
                  className="flex items-center gap-1 text-[10px] themed-text-muted hover:themed-text transition-colors"
                >
                  {tOpen ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
                  <span className="italic">{d != null ? `Thought · ${fmt(d)}` : `Step ${i + 1}`}</span>
                </button>
                {tOpen && (
                  <div className="text-xs themed-text-muted italic pl-3 mt-0.5 whitespace-pre-wrap">
                    {p.content}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export function MessageBubble({
  role, parts, streamingText, streamingReasoning, modelID, personaId, duration, settledCost,
  timeCreated, steer = false, steerUnconfirmed, isComplete = false, showReasoning = true, showToolDetails = true,
  showModelInfo = true, showTimestamps = false, wrapLines,
  messageId, onMessageAction, hasChanges, workspaceId, sessionId,
  isEditing, onEditStart, onEditCancel, onEditSend, onOpenFile, onMoved, renderEditControls,
}: Props) {

  // Edit mode state
  const [editText, setEditText] = useState('')
  const editTextareaRef = useRef<HTMLTextAreaElement>(null)

  // Initialize edit text and focus when entering edit mode
  useEffect(() => {
    if (isEditing && role === 'user') {
      const text = parts
        // Not a CLEARED-FILE note (0353): its text is an English sentence
        // for the model, and editing would send it back as the member's
        // own words — with no marker, so the chip would never come back.
        .filter((p): p is Extract<MessagePart, { type: 'text' }> => p.type === 'text' && !p.cleared)
        .map(p => p.content)
        .join('')
      setEditText(text)
      setTimeout(() => {
        const ta = editTextareaRef.current
        if (ta) {
          ta.focus()
          ta.setSelectionRange(ta.value.length, ta.value.length)
          ta.style.height = 'auto'
          ta.style.height = `${Math.min(ta.scrollHeight, 200)}px`
        }
      }, 0)
    }
  }, [isEditing]) // eslint-disable-line react-hooks/exhaustive-deps

  // When editing, intercept path-insert events dispatched by ChatPanel's
  // drop handler so drive drops land in the edit textarea rather than the
  // main chat input (which is hidden while the edit bubble is active).
  useEffect(() => {
    if (!isEditing || role !== 'user') return
    const onInsert = (e: Event) => {
      const path = (e as CustomEvent<string>).detail
      if (!path) return
      setEditText(prev => prev ? `${prev}\n${path}` : path)
    }
    window.addEventListener('papan:edit-insert-path', onInsert)
    return () => window.removeEventListener('papan:edit-insert-path', onInsert)
  }, [isEditing, role])

  if (role === 'user') {
    const textParts = parts
      .filter((p): p is Extract<MessagePart, { type: 'text' }> => p.type === 'text')
    // 0353: the note standing where a cleared attachment's chip was. It
    // is a `text` part (for the model's sake — see `cleared-attachment.ts`),
    // so it has to be taken OUT of the bubble's own words: the bubble
    // joins every text part into one markdown block, which would drop a
    // stored English sentence into the middle of what the member wrote.
    // Drawn below with the file chips instead, in their own language.
    const clearedParts = textParts.filter(p => p.cleared)
    const textContent = textParts
      .filter(p => !p.cleared)
      .map(p => p.content)
      .join('')
    const fileParts = parts.filter(
      (p): p is Extract<MessagePart, { type: 'file' }> => p.type === 'file',
    )

    // Edit mode: replace bubble with textarea
    if (isEditing && messageId) {
      const handleEditSendClick = () => {
        if (editText.trim() && onEditSend) onEditSend(messageId, editText.trim())
      }
      const handleEditCancelClick = () => onEditCancel?.()

      return (
        <div className="flex gap-2 justify-end">
          <div className="max-w-[85%] flex-1">
            <div className="rounded-lg border themed-border themed-bg-tertiary p-2">
              <textarea
                ref={editTextareaRef}
                value={editText}
                onChange={e => {
                  setEditText(e.target.value)
                  const ta = e.target
                  ta.style.height = 'auto'
                  ta.style.height = `${Math.min(ta.scrollHeight, 200)}px`
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    handleEditSendClick()
                  }
                  if (e.key === 'Escape') {
                    e.preventDefault()
                    handleEditCancelClick()
                  }
                }}
                className="w-full bg-transparent text-sm themed-text outline-none resize-none min-h-[20px]"
                style={{ maxHeight: '200px' }}
              />
              {renderEditControls ? (
                renderEditControls(editText, handleEditCancelClick, handleEditSendClick)
              ) : (
                <div className="flex justify-end mt-1.5">
                  <EditMessageButtons onSend={handleEditSendClick} onCancel={handleEditCancelClick} sendDisabled={!editText.trim()} />
                </div>
              )}
            </div>
          </div>
        </div>
      )
    }

    return (
      <div className="flex gap-2 justify-end group min-w-0">
        {/* Actions — pencil icon + 3-dot menu on hover */}
        {messageId && (onMessageAction || onEditStart) && (
          <div className="opacity-0 group-hover:opacity-100 transition-opacity shrink-0 self-start mt-1 flex items-center gap-0.5">
            {textContent && (
              <CopyButton text={textContent} />
            )}
            {onEditStart && (
              <button
                onClick={() => onEditStart(messageId)}
                className="p-0.5 rounded themed-text-muted hover:themed-text hover:themed-bg-tertiary transition-colors"
                title="Edit message"
              >
                <Pencil size={14} />
              </button>
            )}
            {onMessageAction && (
              <MessageActionsMenu
                messageId={messageId}
                onAction={onMessageAction}
                hasChanges={hasChanges}
              />
            )}
          </div>
        )}
        <div className="max-w-[85%] min-w-0">
          <div className="px-3 py-2 rounded-2xl rounded-br-md bg-brand-600/20 text-sm themed-text">
            {textContent && (
              <MarkdownContent
                content={textContent}
                onPathClick={onOpenFile ? (_id, _isFolder, path, at) => onOpenFile(path, at) : undefined}
              />
            )}
            {(fileParts.length > 0 || clearedParts.length > 0) && (
              <div className={`flex flex-wrap gap-1 ${textContent ? 'mt-1.5' : ''}`}>
                {clearedParts.map(p => (
                  <ClearedAttachmentChip key={p.id} info={p.cleared!} />
                ))}
                {fileParts.map(fp => (
                  <AttachmentChip
                    key={fp.id}
                    attachment={{
                      id: fp.id,
                      filename: fp.filename,
                      mime: fp.mime,
                      url: fp.url || undefined,
                      path: fp.path,
                      // #639 Task 5.2: without this, a sent binary/inline
                      // attachment's chip never got the handle it needed to
                      // become clickable — the field was on the part
                      // (hooks.ts's `file` MessagePart) but was dropped
                      // here on the way into the chip's `Attachment` shape.
                      handle: fp.handle,
                      // #662: a moved attachment carries the drive fileId it
                      // now points at — see AttachmentChip's isMoved logic.
                      fileId: fp.fileId,
                      source: (fp.source as 'paste' | 'drop' | 'drive' | 'attach') || 'attach',
                      status: 'ready',
                    }}
                    onOpen={onOpenFile}
                    sessionId={sessionId}
                    workspaceId={workspaceId}
                    messageId={messageId}
                    onMoved={onMoved}
                    compact
                  />
                ))}
              </div>
            )}
          </div>
          {/* #481 item 4: a steer folded into the running turn can look ignored.
              This labels it so the missing separate reply is explained. Right-
              aligned + muted to match the user bubble; the title says the rest. */}
          {steer ? (
            <div
              className="flex items-center justify-end gap-1 text-[10px] themed-text-muted mt-0.5 mr-1"
              title="You sent this while the assistant was working, so it was added to the running turn rather than answered on its own."
            >
              <CornerDownRight size={10} />
              <span>Steered into this turn</span>
            </div>
          ) : null}
          {steerUnconfirmed ? (
            <div className="flex items-center justify-end gap-1 text-[10px] themed-text-muted mt-0.5 mr-1" title={steerUnconfirmed.hint} data-testid="steer-unconfirmed">
              <span>{steerUnconfirmed.label}</span>
            </div>
          ) : null}
          {/* `? … : null`, not `&&` — a falsy-but-defined timeCreated (0)
              would otherwise render a literal "0" */}
          {showTimestamps && timeCreated ? (
            <div className="text-[9px] themed-text-muted text-right mt-0.5 mr-1">
              {formatTimestamp(timeCreated)}
            </div>
          ) : null}
        </div>
      </div>
    )
  }

  // Assistant message — render parts in order (interleaved tool calls + text)
  type ToolResultPart = Extract<MessagePart, { type: 'tool-result' }>

  const toolResults = new Map<string, ToolResultPart>()
  parts.filter((p): p is ToolResultPart => p.type === 'tool-result').forEach(p => {
    toolResults.set(p.callId, p)
  })

  // Skip tool-result (looked up by callId) and step parts from the main render loop
  const renderableParts = parts.filter(p => p.type !== 'tool-result' && p.type !== 'step')

  // #639: snapshot attachments the assistant's tools produced (preview_screenshot's
  // CAS-uploaded capture). Rendered as clickable chips at the end of the reply so
  // the user can view/download the image — the model's own markdown link to it is
  // stripped by the sanitizer (a session-private handle has no web URL).
  const snapshotAttachments = parts
    .filter((p): p is ToolResultPart => p.type === 'tool-result')
    .flatMap(p => p.attachments ?? [])

  // Don't render the bubble at all if there's nothing to show yet
  const hasContent = renderableParts.length > 0 || streamingText || streamingReasoning
  if (!hasContent) return null

  // Group consecutive tool-call parts into activity clusters (§2 of the
  // redesign doc). Step text / reasoning between tools ends a run — the
  // narration stays interleaved at its natural position.
  type ToolCallPart = Extract<MessagePart, { type: 'tool-call' }>
  type ReasoningPart = Extract<MessagePart, { type: 'reasoning' }>
  type Segment =
    | { kind: 'part'; part: MessagePart }
    | { kind: 'tools'; key: string; parts: ToolCallPart[] }
    | { kind: 'reasoning'; key: string; parts: ReasoningPart[] }
  const segments: Segment[] = []
  for (const part of renderableParts) {
    if (part.type === 'tool-call') {
      const last = segments[segments.length - 1]
      if (last?.kind === 'tools') last.parts.push(part)
      else segments.push({ kind: 'tools', key: part.id, parts: [part] })
    } else if (part.type === 'reasoning') {
      // Consolidate consecutive thoughts into one collapsible cluster (like the
      // tool cluster) so a long multi-step reasoning run is one quiet line.
      const last = segments[segments.length - 1]
      if (last?.kind === 'reasoning') last.parts.push(part as ReasoningPart)
      else segments.push({ kind: 'reasoning', key: part.id, parts: [part as ReasoningPart] })
    } else {
      segments.push({ kind: 'part', part })
    }
  }

  const persona = personaId ? getPersona(personaId) : null

  return (
    <div className="group/msg min-w-0">
      {/* NO author header line — both attempts (purple avatar row, then a
          muted micro-label) added a utility line above the content and hurt
          readability. The persona lives in the hover metadata footer below;
          a real per-author header returns only with the multi-author
          (guidance-AI) feature, designed for that context. */}
      <div className="space-y-1.5 min-w-0">
        {/* Render segments in their natural order */}
        {segments.map(seg => {
          if (seg.kind === 'tools') {
            return (
              <ToolCluster
                key={seg.key}
                parts={seg.parts}
                results={toolResults}
                wrapLines={wrapLines}
                forceInline={!showToolDetails}
                workspaceId={workspaceId}
                onPathClick={onOpenFile}
              />
            )
          }
          if (seg.kind === 'reasoning') {
            if (!showReasoning) return null
            return <ReasoningCluster key={seg.key} parts={seg.parts} />
          }
          const part = seg.part

          if (part.type === 'text') {
            // All assistant text renders at the same readable size. (Previously
            // narration "sandwiched" between two tool calls was shrunk to a
            // `md-chat-step` size, which (a) de-emphasized meaningful mid-turn
            // narration and (b) visibly flipped 14px→small the moment the NEXT
            // tool arrived — read as a "reprint".)
            return (
              <div key={part.id} className="text-sm themed-text">
                <MarkdownContent content={part.content} onPathClick={onOpenFile ? (_id, _isFolder, path, at) => onOpenFile(path, at) : undefined} />
              </div>
            )
          }

          if (part.type === 'compaction') {
            return <CompactionBlock key={part.id} summary={(part as any).summary} />
          }

          if (part.type === 'sync-failed') {
            // A file the agent wrote in the sandbox could NOT be committed to the
            // drive (quota / disk full). Rendered inline and persistently so the
            // failure is never hidden behind the model's "created" narration.
            const sf = part as Extract<MessagePart, { type: 'sync-failed' }>
            // #1453: the PATH in the run's view, never just the name — two different files never look like one
            const filePath = sf.path.replace(/^\/papan\//, '') || sf.path
            return (
              <div
                key={sf.id}
                role="alert"
                className="text-sm rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 flex gap-2 items-start"
              >
                <AlertTriangle size={15} className="text-amber-500 shrink-0 mt-0.5" />
                {/* #1398: a path is one long unbroken token — wrap it anywhere so it never runs past the panel (the
                    reason can name one too, e.g. a move's destination) */}
                <div className="min-w-0 themed-text [overflow-wrap:anywhere]" data-testid="sync-failed-notice-body">
                  <span className="font-medium">Couldn’t save “<span className="break-all">{filePath}</span>” to your drive.</span>{' '}
                  {sf.reason}
                </div>
              </div>
            )
          }

          return null
        })}

        {/* Live streaming reasoning — rendered AFTER the committed reasoning
            parts (not before), so each new "Thinking…" block appends BELOW the
            finished "Thought · Ns" ones instead of stacking upward. */}
        {showReasoning && streamingReasoning && (
          <div className="mb-1">
            <div className="flex items-center gap-1 text-[10px] themed-text-muted">
              <Loader2 size={10} className="animate-spin" />
              <span className="italic">Thinking…</span>
            </div>
            <div className="text-xs themed-text-muted italic border-l-2 border-blue-500/20 pl-2 mt-0.5">
              <StreamingText text={streamingReasoning} isStreaming={true} />
            </div>
          </div>
        )}

        {/* Streaming text (rendered as markdown with inline cursor) */}
        {streamingText?.trim() && (
          <div className="text-sm themed-text">
            <MarkdownContent content={streamingText + ' ▍'} onPathClick={onOpenFile ? (_id, _isFolder, path, at) => onOpenFile(path, at) : undefined} />
          </div>
        )}

        {/* #639: the assistant's snapshot captures, as real inline preview
            images (fetched session-gated), each clickable to open the full
            viewer (Download + Save-to-drive). Replaces the model's dead
            "Open snapshot image" markdown link with the actual picture. */}
        {snapshotAttachments.length > 0 && (
          <div className="flex flex-col gap-1.5">
            {snapshotAttachments.map((a, i) => (
              <SnapshotPreview
                key={a.handle || i}
                handle={a.handle}
                mime={a.mime || 'image/png'}
                filename={a.filename || 'Snapshot'}
                sessionId={sessionId}
                workspaceId={workspaceId}
              />
            ))}
          </div>
        )}

        {/* Per-message metadata footer — @sync OpenCode AssistantMessage lines 1287-1314 */}
        {/* `isComplete`: only once the turn actually finished. `!streamingText`
            alone also goes true mid-turn (between steps / paused awaiting a
            permission), which surfaced the footer before the turn was done.
            TIME leads and stays always visible (user request 2026-07-04);
            model/duration/cost fade in on message hover. Both testids stay
            in the DOM regardless (e2e settle anchors — e2e/CLAUDE.md;
            opacity doesn't affect Playwright visibility/text reads).
            Persona moved to the author header.

            `message-complete` is the TURN-COMPLETE anchor and carries no
            content of its own: it renders on exactly this block's condition,
            which is what "the turn finished" means here. `message-cost` used
            to serve that purpose for four specs that do not test money, and
            it silently stopped being a completion signal the day an unset
            chat-cost visibility began resolving to 'hidden' (0174 follow-up,
            2026-08-18) — the badge is gated on the 'user' view, so those
            specs waited 60s for a turn that had in fact completed. Anchor
            liveness here; anchor on `message-cost` only when the FIGURE is
            the subject. */}
        {showModelInfo && isComplete && !streamingText && (timeCreated || modelID || personaId || (settledCost != null && settledCost.amount > 0)) && (
          <div data-testid="message-complete" className="flex items-center gap-1.5 mt-1.5 text-[10px] themed-text-muted">
            {timeCreated ? <span>{formatTimestamp(timeCreated)}</span> : null}
            <span className="flex items-center gap-1.5 opacity-0 group-hover/msg:opacity-100 transition-opacity duration-150">
              {timeCreated && (persona || modelID || duration || (settledCost != null && settledCost.amount > 0)) ? <span>·</span> : null}
              {persona && (
                <>
                  <span>{persona.name}</span>
                  {(modelID || duration) && <span>·</span>}
                </>
              )}
              {modelID && <span>{modelID}</span>}
              {duration != null && duration > 0 && (
                <>
                  {modelID && <span>·</span>}
                  <span>{formatDuration(duration)}</span>
                </>
              )}
              {settledCost != null && settledCost.amount > 0 && (
                <>
                  {(modelID || duration) && <span>·</span>}
                  <span data-testid="message-cost">
                    {formatMessageCost(settledCost.amount, settledCost.currency)}
                  </span>
                </>
              )}
            </span>
          </div>
        )}

        {/* Standalone timestamp when model info is hidden */}
        {!showModelInfo && showTimestamps && timeCreated && !streamingText ? (
          <div className="text-[9px] themed-text-muted mt-0.5">
            {formatTimestamp(timeCreated)}
          </div>
        ) : null}
      </div>
    </div>
  )
}
